import { Tab, TabList, Tabs } from '@ohear/ui'
import { Link, useRouterState } from '@tanstack/react-router'
/**
 * Tab strip for the My-Account page.
 *
 * Wraps `@ohear/ui` Tabs with router-aware navigation: each tab corresponds
 * to a sub-route (`/account/profile`, `/account/earnings`, ...). Mounting the
 * tab strip inside a sub-route keeps the URL the source of truth and lets the
 * browser back button work as expected.
 */
import type { ReactNode } from 'react'

export type AccountTabId = 'profile' | 'earnings' | 'payment' | 'tax' | 'devices' | 'security'

export interface AccountTabsProps {
  /** Currently active tab id. */
  active: AccountTabId
  /** True for actor accounts; controls visibility of earnings + payment tabs. */
  isActor: boolean
  children: ReactNode
}

interface TabSpec {
  id: AccountTabId
  label: string
  icon?: string
  actorOnly?: boolean
  to: string
}

const TABS: readonly TabSpec[] = [
  { id: 'profile', label: 'Profile', to: '/account/profile' },
  { id: 'earnings', label: 'Earnings', icon: '💰', actorOnly: true, to: '/account/earnings' },
  { id: 'payment', label: 'Payment', icon: '💳', actorOnly: true, to: '/account/payment' },
  // Tax — actor only. Holds W-9 / W-8, encrypted SSN/EIN, loan-out company,
  // payee mailing details, and the per-year 1099 download list.
  { id: 'tax', label: 'Tax', actorOnly: true, to: '/account/tax' },
  { id: 'devices', label: 'Devices', icon: '🎧', to: '/account/devices' },
  { id: 'security', label: 'Security', icon: '🔒', to: '/account/security' },
] as const

export function AccountTabs({ active, isActor, children }: AccountTabsProps) {
  const routerState = useRouterState()
  // The Tabs component manages its own focus / aria; we drive it via `value`
  // so the source of truth is the URL, not local state.
  void routerState

  const visible = TABS.filter((t) => !t.actorOnly || isActor)

  return (
    <Tabs value={active} defaultValue={active}>
      {/* Mobile (<sm): tab strip horizontally scrolls so all items stay reachable
          without forcing a wrap that breaks the underline indicator.
          ≥sm: standard inline tabs. */}
      <TabList
        aria-label="Account sections"
        className="!flex flex-nowrap overflow-x-auto max-w-full w-full sm:w-auto sm:!inline-flex"
      >
        {visible.map((t) => (
          <Link key={t.id} to={t.to} className="no-underline shrink-0">
            <Tab value={t.id}>
              {t.icon ? <span aria-hidden="true">{t.icon}</span> : null}
              <span>{t.label}</span>
            </Tab>
          </Link>
        ))}
      </TabList>
      <div className="pt-4">{children}</div>
    </Tabs>
  )
}
